import { TTSRuntime } from "./tts";

export type CursorSize = "normal" | "large" | "xlarge";

export type AccessibilitySettings = {
  fontScale: number;
  highContrast: boolean;
  underlineLinks: boolean;
  increasedTextSpacing: boolean;
  reduceMotion: boolean;
  enhancedKeyboardNav: boolean;
  enhancedFocusIndicators: boolean;
  cursorSize: CursorSize;
  ttsOnFocus: boolean;
  ttsOnHover: boolean;
  screenReaderOptimization: boolean;
  dyslexiaFriendlyFont: boolean;
  // Newly implemented for Voice Accessibility
  ttsEnabled: boolean;
  speechRate: number;
};

export const DEFAULTS: AccessibilitySettings = {
  fontScale: 1.0,
  highContrast: false,
  underlineLinks: false,
  increasedTextSpacing: false,
  reduceMotion: false,
  enhancedKeyboardNav: false,
  enhancedFocusIndicators: false,
  cursorSize: "normal",
  ttsOnFocus: false,
  ttsOnHover: false,
  screenReaderOptimization: false,
  dyslexiaFriendlyFont: false,
  // Newly implemented for Voice Accessibility
  ttsEnabled: false,
  speechRate: 1.0,
};

export const MIN_SCALE = 0.75;
export const MAX_SCALE = 1.5;
export const clamp2 = (n: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(n.toFixed(2))));

export const GLOBAL_STORAGE_KEY = "landing.settings.global";

export function applySettingsToCSS(settings: AccessibilitySettings) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  root.style.setProperty("--font-scale", String(clamp2(settings.fontScale)));

  // Example tokens already in project:
  toggleClass(root, "a11y-underline-links", settings.underlineLinks);
  toggleClass(root, "a11y-text-spacing", settings.increasedTextSpacing);
  toggleClass(root, "a11y-reduce-motion", settings.reduceMotion);
  toggleClass(root, "a11y-enhanced-kbd", settings.enhancedKeyboardNav);
  toggleClass(root, "a11y-enhanced-focus", settings.enhancedFocusIndicators);
  toggleClass(root, "a11y-sr-optimized", settings.screenReaderOptimization);
  toggleClass(root, "a11y-dyslexia-friendly", settings.dyslexiaFriendlyFont);

  root.style.setProperty(
    "--cursor-scale",
    settings.cursorSize === "large" ? "1.35" : settings.cursorSize === "xlarge" ? "1.7" : "1"
  );

  // Contrast tokens (kept from original)
  if (settings.highContrast) {
    root.style.setProperty("--text-color", "#000000");
    root.style.setProperty("--bg-color", "#ffffff");
    root.style.setProperty("--link-color", "#0000EE");
    root.style.setProperty("--border-color", "#000000");
    root.style.setProperty("--focus-ring", "rgba(0, 0, 0, 0.85)");
  } else {
    root.style.setProperty("--text-color", "#0f172a");
    root.style.setProperty("--bg-color", "#ffffff");
    root.style.setProperty("--link-color", "#2563eb");
    root.style.setProperty("--border-color", "#e5e7eb");
    root.style.setProperty("--focus-ring", "rgba(37, 99, 235, 0.55)");
  }
}

function toggleClass(el: Element, cls: string, on: boolean) {
  if (on) el.classList.add(cls);
  else el.classList.remove(cls);
}

// Track transitions to call readPage only on OFF -> ON
let lastMasterOn = false;

/**
 * Called by your hook on every settings change.
 * - Syncs rate
 * - Binds/unbinds hover/focus/selection based on their own toggles
 * - Reads page once when master TTS turns ON
 *
 * Newly implemented for Voice Accessibility
 */
export function enableTTS(settings: AccessibilitySettings) {
  if (typeof window === "undefined") return;

  // Always sync rate (clamped)
  const rate = Math.max(0.5, Math.min(2.0, Number(settings.speechRate) || 1.0));
  TTSRuntime.setRate(rate);

  // DECOPLED: Focus/Hover/Selection bindings are controlled by their own toggles,
  // NOT by the master ttsEnabled flag.
  if (settings.ttsOnFocus) {
    TTSRuntime.bindFocus();
    TTSRuntime.bindSelection(); // selection read helps when plain text is highlighted
  } else {
    TTSRuntime.unbindFocus();
    TTSRuntime.unbindSelection();
  }

  if (settings.ttsOnHover) {
    TTSRuntime.bindHover();
  } else {
    TTSRuntime.unbindHover();
  }

  // Master TTS only controls reading the page once when it turns ON
  if (settings.ttsEnabled) {
    if (!lastMasterOn) {
      // transitioned OFF -> ON
      TTSRuntime.readPage();
    }
    lastMasterOn = true;
  } else {
    // turning master OFF does NOT disable focus/hover readers anymore.
    // We only stop any ongoing speech spawned by page read.
    TTSRuntime.stop();
    lastMasterOn = false;
  }
}

export { TTSRuntime } from "./tts";
